import * as THREE from "three";
import Chance from "chance";

const HAS_AFRAME = typeof window !== "undefined" && typeof window.AFRAME !== "undefined" && !!window.AFRAME;
const isAFrameEntity = v => !!(v && typeof v.addEventListener === "function" && v.object3D);
const isObject3D = v => !!(v && (v.isObject3D || v instanceof THREE.Object3D));

const chance = new Chance();
const assignRandomNameIfMissing = (obj, prefix = "scriptable") => {
  if (!obj || obj.name) return;

  try {
    obj.name = `${prefix}-${chance.word({ length: 6 })}`;
  } catch (_) {
    obj.name = `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
  }
};

function getChildEntityEl(child, parentEl) {
  const childEl = child?.el || child?.__el || null;
  if (!childEl) return null; // raw THREE child
  if (childEl === parentEl) return null; // same entity (internal node)
  if (childEl.contains(parentEl)) return null; // would create cycle
  return childEl;
}

export class ScriptableObjects {
  static _objectsToWrapperRegistry = new WeakMap();
  static _wrappersToObjectsRegistry = new WeakMap();
  static _factory = new Map();
  static _editor = null;

  static get editor() {
    return typeof globalThis !== "undefined" && globalThis.editor || this._editor;
  }

  static set editor(editor) {
    this._editor = editor;
  }

  static registerType(type, factory) {
    if (!type || typeof factory !== "function") return;
    this._factory.set(type, factory);
  }

  static _noteWrap(sourceObject, wrapper) {
    try {
      this._objectsToWrapperRegistry.set(sourceObject, wrapper);
      this._wrappersToObjectsRegistry.set(wrapper, sourceObject);
    } catch (_) {
      /* ignore */
    }
  }

  // Promote by scriptableType if possible
  static makeScriptable(obj) {
    if (!obj) return obj;

    const scriptableType = obj?.scriptableType;

    if (!scriptableType) return obj;

    if (obj.isScriptableObject) return obj;

    if (this._objectsToWrapperRegistry.has(obj)) {
      const entry = this._objectsToWrapperRegistry.get(obj);
      return entry || obj;
    }

    const factory = this._factory.get(scriptableType);

    if (typeof factory === "function") {
      const wrapper = factory(obj.el && isAFrameEntity(obj.el) ? obj.el : obj) || obj;
      this._noteWrap(obj, wrapper);
      return wrapper;
    }
    return obj;
  }
}

// Generic DFS finder that works for Scene, Object3D, and ScriptableObject.
// Always returns a scriptable-promoted object when possible.
export function findObjectByProperty(root, propertyName, value, debug = false) {
  if (!root || !propertyName) return null;

  const visit = current => {
    if (!current) return null;

    // Prefer wrapper match when available
    if (current[propertyName] === value) {
      const obj = ScriptableObjects.makeScriptable(current);
      if (debug) console.log("findObjectByProperty", obj, obj[propertyName], value);
      return obj;
    }

    const children = current.children || [];
    for (let i = 0; i < children.length; i++) {
      const result = visit(children[i]);
      if (result) return result;
    }

    return null;
  };

  return visit(root);
}

// Reusable traversal helpers that attempt to wrap each visited object
export function traverseDepthFirst(root, callback) {
  if (!root || typeof callback !== "function") return;

  const wrapped = ScriptableObjects.makeScriptable(root) || root;
  callback(wrapped);

  const children = root.children || [];
  for (let i = 0, l = children.length; i < l; i++) {
    traverseDepthFirst(children[i], callback);
  }
}

export function traverseVisibleDepthFirst(root, callback) {
  if (!root || typeof callback !== "function") return;
  if (root.visible === false) return;

  const wrapped = ScriptableObjects.makeScriptable(root) || root;
  callback(wrapped);

  const children = root.children || [];
  for (let i = 0, l = children.length; i < l; i++) {
    traverseVisibleDepthFirst(children[i], callback);
  }
}

export function traverseAncestorsWalk(root, callback) {
  if (!root || typeof callback !== "function") return;
  let parent = root.parent || null;
  while (parent !== null) {
    const wrapped = ScriptableObjects.makeScriptable(parent) || parent;
    callback(wrapped);
    parent = parent.parent || null;
  }
}

export function patchObject3DFinders() {
  try {
    const O3D = typeof THREE !== "undefined" && THREE && THREE.Object3D ? THREE.Object3D : null;
    if (!O3D || !O3D.prototype) return;

    O3D.prototype.getObjectByProperty = function (propertyName, value) {
      return findObjectByProperty(this, propertyName, value);
    };

    O3D.prototype.getObjectById = function (id) {
      return this.getObjectByProperty("id", id);
    };

    O3D.prototype.getObjectByName = function (name) {
      return this.getObjectByProperty("name", name);
    };

    O3D.prototype.getObjectByUUID = function (uuid) {
      return this.getObjectByProperty("uuid", uuid);
    };
  } catch (_) {
    /* ignore */
  }
}

// Patch THREE.Object3D traverse methods to auto-wrap visited objects
export function patchObject3DTraversals() {
  try {
    const O3D = typeof THREE !== "undefined" && THREE && THREE.Object3D ? THREE.Object3D : null;
    if (!O3D || !O3D.prototype) return;

    O3D.prototype.traverse = function (callback) {
      traverseDepthFirst(this, callback);
    };

    O3D.prototype.traverseVisible = function (callback) {
      traverseVisibleDepthFirst(this, callback);
    };

    O3D.prototype.traverseAncestors = function (callback) {
      traverseAncestorsWalk(this, callback);
    };
  } catch (_) {
    /* ignore */
  }
}

const getParentEl = object => {
  if (!object) return null;

  if (HAS_AFRAME) {
    // Traverse ancestors until we find an entity with an el
    let parent = object.parent;
    while (parent) {
      if (parent.el) return parent.el;
      parent = parent.parent;
    }
  }
  return null;
};

// Centralized helper to handle add/attach-like behavior with A-Frame interop
// Appends child entity els to a provided root element when appropriate; otherwise
// defers to the provided original function. Returns the supplied returnValue.
export function processAddLike(
  objects,
  { rootEl, originalFn, requireScriptable = false, respectThreeParent = false, returnValue } = {}
) {
  if (!originalFn) return returnValue;
  if (!HAS_AFRAME || !rootEl) {
    originalFn(...objects);
    return returnValue;
  }

  const passthrough = [];

  objects.forEach(obj => {
    if (!obj) return;

    const isScriptable = !requireScriptable || obj.isScriptableObject || obj.scriptableType;
    if (!isScriptable) {
      passthrough.push(obj);
      return;
    }

    const childEl = getChildEntityEl(obj, rootEl);
    if (!childEl) {
      passthrough.push(obj);
      return;
    }

    if ((respectThreeParent && obj.parent) || childEl.parentElement) {
      passthrough.push(obj);
      return;
    }

    try {
      rootEl.appendChild(childEl);
    } catch (_) {
      // If anything goes wrong, fall back to native behavior
      passthrough.push(obj);
    }
  });

  if (passthrough.length) {
    originalFn(...passthrough);
  }

  return returnValue;
}

export function patchObject3DAddAttach(root) {
  try {
    if (!HAS_AFRAME) return;
    const O3D = typeof THREE !== "undefined" && THREE && THREE.Object3D ? THREE.Object3D : null;
    if (!O3D || !O3D.prototype) return;

    const el = getParentEl(this) || root;

    const origAdd = O3D.prototype.add;
    const origAttach = O3D.prototype.attach;

    O3D.prototype.add = function (...objects) {
      const callOriginal = (...args) => origAdd.apply(this, args);
      return processAddLike(objects, {
        rootEl: el,
        originalFn: callOriginal,
        requireScriptable: true,
        respectThreeParent: false,
        returnValue: this
      });
    };

    O3D.prototype.attach = function (...objects) {
      if (!root) {
        return origAttach(...objects);
      }
      const callOriginal = (...args) => origAttach.apply(this, args);
      return processAddLike(objects, {
        rootEl: el,
        originalFn: callOriginal,
        requireScriptable: true,
        respectThreeParent: false,
        returnValue: this
      });
    };
  } catch (_) {
    console.error("error patching add and attach", _);
    /* ignore */
  }
}

export function logicalChildrenOf(src) {
  // Works in A-Frame and editor/plain Three.
  const out = [];
  const seen = new Set();
  const push = o => {
    if (!o || seen.has(o.uuid)) return;
    seen.add(o.uuid);
    // Ensure we operate on scriptable wrappers when available:
    out.push(ScriptableObjects.makeScriptable(o) || o);
  };

  if (HAS_AFRAME && src.el) {
    // 1) DOM entity children (object3D might not be reparented yet, but it exists)
    for (const childEl of src.el.children) {
      if (childEl.object3D) push(childEl.object3D);
    }
    // 2) Raw THREE children (non-entity) already under this entity’s object3D
    const o3d = src.el.object3D;
    if (o3d?.children?.length) {
      for (const c of o3d.children) {
        // avoid duplicates: skip entity-backed children
        if (!c.el && !c.__el) push(c);
      }
    }
  } else {
    // Editor/plain Three path
    if (Array.isArray(src.children)) for (const c of src.children) push(c);
  }

  return out;
}

export class ScriptableObject extends ScriptableMixin(THREE.Object3D) {}

// TODO: add a random name to the object3D using chance.js
export function ScriptableMixin(Base = THREE.Object3D) {
  return class Scriptable extends Base {
    get el() {
      return this.__el || null;
    }

    get isScriptableObject() {
      return true;
    }

    static setEditor(editor) {
      this.editor = editor;
    }

    getObjectByProperty(propertyName, value) {
      return findObjectByProperty(this, propertyName, value);
    }

    getObjectByName(name) {
      return this.getObjectByProperty("name", name);
    }

    getObjectById(id) {
      // Prefer the generic property search for consistency
      if (super.getObjectById) {
        const found = super.getObjectById(id);
        if (!found) return null;
        return ScriptableObjects.makeScriptable(found);
      }
      return this.getObjectByProperty("id", id);
    }

    getObjectByUUID(uuid) {
      return this.getObjectByProperty("uuid", uuid);
    }

    add(...objects) {
      const el = this.el;
      const callOriginal = (...args) => super.add(...args);
      return processAddLike(objects, {
        rootEl: el,
        originalFn: callOriginal,
        requireScriptable: false,
        respectThreeParent: false,
        returnValue: this
      });
    }

    attach(...objects) {
      const el = this.el;
      if (!HAS_AFRAME || !el) {
        return super.attach(...objects);
      }

      objects.forEach(object => {
        if (!object) return;
        const childEl = getChildEntityEl(object, el);

        // Do not reparent the element if already in DOM because aframe doesn't allow it
        if (childEl && !childEl.parentElement) {
          el.appendChild(childEl);
        } else {
          super.attach(object);
        }
      });

      return this;
    }

    // Traverse methods that auto-wrap like THREE defaults
    traverse(callback) {
      traverseDepthFirst(this, callback);
    }

    traverseVisible(callback) {
      traverseVisibleDepthFirst(this, callback);
    }

    traverseAncestors(callback) {
      traverseAncestorsWalk(this, callback);
    }

    // Prototype-shift the entity's root object3D to this class (identity preserved)
    static adopt(target) {
      if (!target) return null;

      if (isAFrameEntity(target)) {
        const el = target;
        const obj = el.object3D;

        // Idempotent: already this type
        if (obj instanceof this) {
          if (!obj.__el) Object.defineProperty(obj, "__el", { value: el, configurable: true });
          ScriptableObjects._noteWrap(obj, obj);
          assignRandomNameIfMissing(obj, this.scriptableType || "so");
          return obj;
        }

        const type = this.scriptableType;
        if (type && obj && obj.scriptableType && obj.scriptableType !== type) return obj;

        Object.setPrototypeOf(obj, this.prototype);
        Object.defineProperty(obj, "__el", { value: el, configurable: true });

        ScriptableObjects._noteWrap(obj, obj);
        assignRandomNameIfMissing(obj, this.scriptableType || "so");

        // Re-apply if A-Frame swaps root object3D later
        const reapply = e => {
          const t = e?.detail?.type || e?.detail;
          if (t === "object3D") {
            el.removeEventListener("object3dset", reapply);
            this.adopt(el);
          }
        };
        el.addEventListener("object3dset", reapply);

        return obj;
      }

      if (isObject3D(target)) {
        const obj = target;

        if (obj instanceof this) {
          ScriptableObjects._noteWrap(obj, obj);
          assignRandomNameIfMissing(obj, this.scriptableType);
          return obj;
        }

        const type = this.scriptableType;
        if (type && obj.scriptableType && obj.scriptableType !== type) return obj;

        Object.setPrototypeOf(obj, this.prototype);

        ScriptableObjects._noteWrap(obj, obj);
        assignRandomNameIfMissing(obj, this.scriptableType);
        return obj;
      }

      return target;
    }
  };
}

export function patchedSceneAddFactory(scene, { sceneEl } = {}) {
  const originalAdd = scene.add.bind(scene);
  const root = sceneEl || (HAS_AFRAME ? window.AFRAME.scenes[0] : null);

  return function patchedSceneAdd(...objects) {
    const callOriginal = (...args) => originalAdd(...args);
    return processAddLike(objects, {
      rootEl: root,
      originalFn: callOriginal,
      requireScriptable: false,
      respectThreeParent: true,
      returnValue: scene
    });
  };
}

export function patchedSceneAttachFactory(scene, { sceneEl } = {}) {
  const originalAttach = scene.attach ? scene.attach.bind(scene) : null;
  const root = sceneEl || (HAS_AFRAME ? window.AFRAME.scenes[0] : null);

  return function patchedSceneAttach(...objects) {
    if (!HAS_AFRAME || !root || !originalAttach) {
      if (originalAttach) return originalAttach(...objects);
    }

    objects.forEach(obj => {
      if (!obj) return;
      const el = obj?.el || obj?.__el || null;
      if (!el) {
        originalAttach(obj);
        return;
      }

      // If the object already has a parent in DOM, avoid reparenting; Aframe doesn't allow it
      if (obj.el.parentElement) {
        originalAttach(obj);
        return;
      }

      if (el !== root && !el.contains(root)) {
        root.appendChild(el);
      }
    });

    return scene;
  };
}

// [WARNING] removing the element from the DOM can cause unexpected behavior in the three default logic
export function patchedSceneRemoveFactory(scene, { sceneEl } = {}) {
  const originalRemove = scene.remove.bind(scene);
  const root = sceneEl || (HAS_AFRAME ? window.AFRAME.scenes[0] : null);

  return function patchedSceneRemove(...objects) {
    if (!HAS_AFRAME || !root) {
      originalRemove(...objects);
      return scene;
    }

    objects.forEach(obj => {
      if (!obj) return;
      const el = obj?.el || obj?.__el || null;
      if (!el) {
        originalRemove(obj);
        return;
      }

      if (root.contains(el)) {
        try {
          root.removeChild(el);
        } catch (_) {
          /* ignore */
        }
      }
    });

    return scene;
  };
}

export function patchedSceneGetObjectByNameFactory(scene) {
  return function patchedGetObjectByName(name) {
    return findObjectByProperty(scene, "name", name, false);
  };
}

// Patch scene.getObjectByProperty to auto-promote returned instances
export function patchedSceneGetObjectByPropertyFactory(scene) {
  return function patchedGetObjectByProperty(propertyName, value) {
    return findObjectByProperty(scene, propertyName, value);
  };
}

export function patchedSceneGetObjectByIdFactory(scene) {
  return function patchedGetObjectById(id) {
    return findObjectByProperty(scene, "id", id);
  };
}

export function patchedSceneGetObjectByUUIDFactory(scene) {
  return function patchedGetObjectByUUID(uuid) {
    return findObjectByProperty(scene, "uuid", uuid);
  };
}

// Scene traverse patches using the reusable helpers
export function patchedSceneTraverseFactory(scene) {
  return function patchedTraverse(callback) {
    traverseDepthFirst(scene, callback);
    return scene;
  };
}

export function patchedSceneTraverseVisibleFactory(scene) {
  return function patchedTraverseVisible(callback) {
    traverseVisibleDepthFirst(scene, callback);
    return scene;
  };
}

export function patchedSceneTraverseAncestorsFactory(scene) {
  return function patchedTraverseAncestors(callback) {
    traverseAncestorsWalk(scene, callback);
    return scene;
  };
}

ScriptableObject.mediaFrameEcsInit = null;
ScriptableObject.setMediaFrameEcsInit = function (fn) {
  this.mediaFrameEcsInit = typeof fn === "function" ? fn : null;
};

ScriptableObject.snapColliderEcsInit = null;
ScriptableObject.setSnapColliderEcsInit = function (fn) {
  this.snapColliderEcsInit = typeof fn === "function" ? fn : null;
};

ScriptableObject.mediaFrameEcsAccess = null;
ScriptableObject.setMediaFrameEcsAccess = function (access) {
  this.mediaFrameEcsAccess = access && typeof access === "object" ? access : null;
};

ScriptableObject.setMediaInflator = function (inflator) {
  this.mediaInflator = inflator;
};

ScriptableObject.setRuntimeMode = function (mode) {
  this.runtimeMode = mode;
};
